import { HttpStatusCode } from 'axios';
import { call, put } from 'redux-saga/effects';
import { z } from 'zod';

import config from 'app.config';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { axiosInstance, HTTP_METHOD } from 'utils/request';

import { SagaHandler } from '../SagaHandler';

// Statuses that shouldn't throw an error
const ALLOWED_STATUSES = [HttpStatusCode.Ok, HttpStatusCode.Conflict];
const apiValidateReservedAppointmentSlot = async ({
  providerId,
  slotId,
}) => axiosInstance({
  method: HTTP_METHOD.POST,
  baseURL: config.API_URL,
  url: `provider/${providerId}/reservedAppointmentSlot/${slotId}/validate`,
  validateStatus: (status) => ALLOWED_STATUSES.includes(status),
});

const requestBodySchema = z.object({
  providerId: z.number(),
  slotId: z.number(),
});

const validateSlot = function* (payload) {
  const response = yield call(apiValidateReservedAppointmentSlot, payload);

  // Should never happen since we configured the axios instance, but just in case.
  if (!ALLOWED_STATUSES.includes(response.status)) {
    throw new Error('Invalid response status');
  }

  const isSlotAvailable = response.status === HttpStatusCode.Ok;
  return { isSlotAvailable };
};

/*
  Validates a reserved appointment slot
*/
class _ValidateReservedAppointmentSlot extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/VALIDATE_RESERVED_APPOINTMENT_SLOT',
      stateKey: 'validateReservedAppointmentSlot',
    });
  }

  /**
   * @returns {Promise<{ isSlotAvailable: boolean }>}
   */
  * saga({ payload }) {
    try {
      const parsedPayload = requestBodySchema.parse(payload);

      yield put(this.loading.update(true));
      yield put(this.error.update(null));
      yield put(this.success.update(null));
      yield put(this.value.update(null));

      const result = yield call(validateSlot, parsedPayload);
      yield put(this.value.update(result));
    } catch (error) {
      sendToLogger(error, 'validateReservedAppointmentSlot');
      const errorMessage = extractErrorMessage(error);
      yield put(this.error.update(errorMessage));
      yield put(this.success.update(false));
    } finally {
      yield put(this.success.update(true));
      yield put(this.loading.update(false));
    }
  }
}

export const ValidateReservedAppointmentSlot = new _ValidateReservedAppointmentSlot();
